// ═══════════════════════════════════════════════════════════
// GAB Notifications — drop-in replacement for @mantine/notifications
// Same API: notifications.show({ title, message, color, autoClose })
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface NotificationData {
  id: string;
  title?: string;
  message?: React.ReactNode;
  color?: string;
  autoClose?: number | false;
  loading?: boolean;
  icon?: React.ReactNode;
  withCloseButton?: boolean;
}

type ShowOptions = Omit<NotificationData, 'id'> & { id?: string };

// ── Singleton store ──────────────────────────────────────

let _items: NotificationData[] = [];
const _listeners = new Set<() => void>();
const _timers = new Map<string, ReturnType<typeof setTimeout>>();

function notify() { _listeners.forEach(l => l()); }

function scheduleClose(id: string, ms: number) {
  if (_timers.has(id)) clearTimeout(_timers.get(id)!);
  _timers.set(id, setTimeout(() => notifications.hide(id), ms));
}

export const notifications = {
  show(opts: ShowOptions): string {
    const id = opts.id ?? Math.random().toString(36).slice(2);
    // If same ID already exists, update instead
    if (opts.id && _items.find(i => i.id === id)) {
      notifications.update({ ...opts, id });
      return id;
    }
    _items = [..._items, { withCloseButton: true, ...opts, id }];
    notify();
    const delay = opts.autoClose !== false ? (typeof opts.autoClose === 'number' ? opts.autoClose : 5000) : null;
    if (delay) scheduleClose(id, delay);
    return id;
  },

  update(opts: Partial<NotificationData> & { id: string }) {
    _items = _items.map(x => x.id === opts.id ? { ...x, ...opts } : x);
    notify();
    if (opts.autoClose !== false && opts.autoClose) scheduleClose(opts.id, opts.autoClose as number);
  },

  hide(id: string) {
    if (_timers.has(id)) { clearTimeout(_timers.get(id)!); _timers.delete(id); }
    _items = _items.filter(x => x.id !== id);
    notify();
  },

  clean() {
    _timers.forEach(t => clearTimeout(t)); _timers.clear();
    _items = []; notify();
  },

  cleanQueue() { /* no-op — compat */ },
};

// ── Color → CSS class mapping ────────────────────────────

function toastClass(color?: string) {
  const map: Record<string, string> = {
    green: 'toast-success', teal: 'toast-success',
    red: 'toast-error', pink: 'toast-error',
    yellow: 'toast-warning', orange: 'toast-warning',
    blue: 'toast-blue', cyan: 'toast-info',
    grape: 'toast-primary', violet: 'toast-primary',
    gray: '', dark: '',
  };
  if (!color) return '';
  return map[color] ?? `toast-${color}`;
}

// ── Toast item ───────────────────────────────────────────

function Toast({ item }: { item: NotificationData }) {
  return (
    <div className={`gab-toast ${toastClass(item.color)}`} role="alert">
      {item.icon && <div className="gab-toast-icon">{item.icon}</div>}
      {item.loading && (
        <div className="gab-toast-icon">
          <div className="spinner spinner-sm" style={{ borderTopColor: 'var(--color-primary-600)' }} />
        </div>
      )}
      <div className="gab-toast-body">
        {item.title && <div className="gab-toast-title">{item.title}</div>}
        {item.message && <div className="gab-toast-message">{item.message}</div>}
      </div>
      {item.withCloseButton !== false && (
        <button className="gab-toast-close" onClick={() => notifications.hide(item.id)} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ── Notifications container ──────────────────────────────

export function Notifications({ position = 'top-right', limit = 5 }: { position?: string; limit?: number; zIndex?: number }) {
  const [items, setItems] = useState<NotificationData[]>(_items);

  useEffect(() => {
    const listener = () => setItems([..._items]);
    _listeners.add(listener);
    return () => { _listeners.delete(listener); };
  }, []);

  const visible = items.slice(-limit);

  const posStyle: React.CSSProperties = { position: 'fixed', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: 380, pointerEvents: 'none' };
  if (position.includes('top')) posStyle.top = '1rem'; else posStyle.bottom = '1rem';
  if (position.includes('right')) posStyle.right = '1rem'; else if (position.includes('left')) posStyle.left = '1rem';
  else { posStyle.left = '50%'; posStyle.transform = 'translateX(-50%)'; }

  return createPortal(
    <div style={posStyle}>
      {visible.map(item => <Toast key={item.id} item={item} />)}
    </div>,
    document.body,
  );
}
